import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { Router } from '@angular/router';
import { NetworkGroupApiService } from '@fleet/api/network-group/network-group-api.service';
import { AuthService } from '@fleet/auth/services/auth.service';
import { NetworkGroupModel, NetworkModel } from '@fleet/model';
import { NetworkGroupService } from '@fleet/network-group';
import { Observable } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  tap,
} from 'rxjs/operators';

import { toObservable } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ProductConfigurationService } from '@fleet/product-configuration';
import { NetworkGroupHeaderService } from '@fleet/token';
import { combineLatest } from 'rxjs';

export type NetworkGroupSwitcherMode = 'dropdown' | 'fullpage';

@Component({
  selector: 'fleet-network-group-switcher',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule,
    MatDividerModule,
    ReactiveFormsModule,
    MatMenuModule,
    MatButtonModule,
    MatTooltipModule,
  ],
  template: `
    <!-- Dropdown Mode -->
    <div
      *ngIf="mode === 'dropdown' && (showSelector$ | async)"
      class="flex items-center"
    >
      <div
        [matMenuTriggerFor]="isLocked ? null : networkGroupMenu"
        [class]="
          'min-w-0 px-2 h-10 flex items-center ' +
          (isLocked ? 'cursor-not-allowed' : 'cursor-pointer')
        "
        [matTooltip]="tooltipText"
      >
        <div class="flex items-center">
          <!-- Show network logo if available -->
          <img
            *ngIf="(network$ | async)?.logoUrl"
            [src]="(network$ | async)?.logoUrl"
            class="w-7 h-7 rounded-full mr-2"
            alt="Network logo"
          />

          <!-- Show selected group name or network name -->
          <span class="font-medium truncate">
            {{
              selectedGroup()?.displayName ||
                attributionGroup()?.displayName ||
                (network$ | async)?.displayName
            }}
          </span>

          <!-- Modified dropdown/lock icon -->
          <mat-icon
            class="ml-1 icon-size-3"
            [ngClass]="{
              'text-gray-400': isLocked,
              
            }"
            [svgIcon]="
              isLocked
                ? 'heroicons_solid:lock-closed'
                : 'heroicons_solid:chevron-down'
            "
          ></mat-icon>
        </div>
      </div>

      <!-- Network Group Menu -->
      <mat-menu
        #networkGroupMenu="matMenu"
        [xPosition]="'after'"
        class="network-group-menu"
      >
        <!-- Network option -->
        <button
          mat-menu-item
          *ngIf="network$ | async as network"
          (click)="selectNetwork()"
          [class.selected]="selectedGroup() === null"
        >
          <div class="flex items-center">
            <img
              *ngIf="network.logoUrl"
              [src]="network.logoUrl"
              class="w-6 h-6 rounded-full mr-2"
              alt="Network logo"
            />
            <span class="font-medium">{{ network.displayName }}</span>
          </div>
        </button>

        <mat-divider></mat-divider>

        <!-- Group header -->
        <div class="group-header px-4 py-2 text-sm">Fleets</div>

        <!-- Network groups -->
        <button
          mat-menu-item
          *ngFor="let group of availableGroups()"
          (click)="selectGroup(group)"
          [class.selected]="
            selectedGroup()?.networkGroupId === group.networkGroupId
          "
        >
          <div class="flex items-center">
            <img
              *ngIf="group.logoUrl"
              [src]="group.logoUrl"
              class="w-6 h-6 rounded-full mr-2"
              alt="Group logo"
            />
            <span>{{ group.displayName }}</span>
          </div>
        </button>
      </mat-menu>
    </div>

    <!-- Fullpage Mode -->
    <div
      *ngIf="mode === 'fullpage'"
      class="fixed inset-0 flex items-center justify-center bg-white"
    >
      <div class="max-w-2xl w-full mx-auto p-8">
        <h1 class="text-3xl font-bold text-center mb-8">Select your Fleet</h1>

        <!-- Network Groups List -->
        <div class="space-y-4">
          <button
            *ngFor="let group of availableGroups()"
            (click)="selectGroupAndNavigate(group)"
            class="w-full p-4 flex items-center bg-white border rounded-lg hover:bg-gray-50 transition-colors duration-150"
          >
            <img
              *ngIf="group.logoUrl"
              [src]="group.logoUrl"
              class="w-10 h-10 rounded-full mr-4"
              alt="Group logo"
            />
            <div class="flex-1">
              <h3 class="text-lg font-medium">{{ group.displayName }}</h3>
            </div>
            <mat-icon
              class="text-gray-400"
              svgIcon="heroicons_solid:chevron-right"
            ></mat-icon>
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      ::ng-deep .network-group-menu {
        max-height: 80vh !important;
      }

      ::ng-deep .group-header {
        font-weight: 500;
        color: #666;
        background-color: #f5f5f5;
      }

      ::ng-deep .selected {
        background-color: rgba(0, 0, 0, 0.04);
      }

      button.mat-mdc-button {
        height: 40px;
      }

      .truncate {
        max-width: 200px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      button.override-disabled-button[disabled] {
        opacity: 1 !important;
        background-color: #3f51b5 !important;
        color: red !important;
        cursor: pointer !important;
      }
    `,
  ],
})
export class NetworkGroupSwitcherComponent {
  @Input() mode: NetworkGroupSwitcherMode = 'fullpage';

  network = signal<NetworkModel>(null);
  availableGroups = signal<NetworkGroupModel[]>([]);
  selectedGroup = signal<NetworkGroupModel>(null);
  attributionGroup = signal<NetworkGroupModel>(null);
  private destroyRef = inject(DestroyRef);

  network$ = this.networkGroupService.network$;
  showSelector$: Observable<boolean>;
  isLocked: boolean;
  tooltipText: string;

  constructor(
    private networkGroupService: NetworkGroupService,
    private authService: AuthService,
    private networkGroupApiService: NetworkGroupApiService,
    private productConfigurationService: ProductConfigurationService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private networkGroupHeaderService: NetworkGroupHeaderService
  ) {
    console.log('NetworkGroupSwitcherComponent constructor called');

    //Set up the show selector observable
    this.showSelector$ = combineLatest([
      toObservable(this.availableGroups),
      this.productConfigurationService.networkConfig$,
    ]).pipe(
      map(([groups, networkConfig]) => {
        if (!groups || !networkConfig) return false;
        return groups.length > 0 && !networkConfig.networkGroupId;
      })
    );

    this.productConfigurationService.networkConfig$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((networkConfig) => networkConfig && networkConfig.networkId),
        switchMap((networkConfig) =>
          this.networkGroupApiService.searchNetworkGroups(
            {},
            networkConfig.networkId
          )
        ),
        map((response) => response.body.data)
      )
      .subscribe((groups) => {
        this.availableGroups.set(groups);
        this.cdr.markForCheck();
      });

    // Subscribe to the network group service's selected group
    this.networkGroupService.networkGroup$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((group) => {
          console.log('Network group changed:', {
            groupId: group?.networkGroupId,
            displayName: group?.displayName,
            previousGroup: this.selectedGroup(),
          });
        }),
        distinctUntilChanged(
          (prev, curr) => prev?.networkGroupId === curr?.networkGroupId
        )
      )
      .subscribe((selectedGroup) => {
        if (selectedGroup) {
          // Find the matching group in available groups
          const availableGroups = this.availableGroups();
          const matchingGroup = availableGroups.find(
            (g) => g.networkGroupId === selectedGroup.networkGroupId
          );

          // Update the selected group
          if (matchingGroup) {
            console.log('Setting selected group:', {
              groupId: matchingGroup.networkGroupId,
              displayName: matchingGroup.displayName,
            });
            this.selectedGroup.set(matchingGroup);
          } else {
            console.log('Clearing selected group - no matching group found');
            this.selectedGroup.set(null);
          }
        } else {
          // Set to null (network)
          console.log('Clearing selected group - network selected');
          this.selectedGroup.set(null);
        }
        this.cdr.markForCheck();
      });

    // Subscribe to auth user changes
    this.authService.user$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((user) => !!user),
        filter((user) => this.authService.userType === 'NETWORK'),
        switchMap((user) => {
          const networkId = this.authService.networkId;
          console.log('Fetching network groups for network:', networkId);

          // Get network
          return this.networkGroupApiService
            .searchNetworkGroups({}, networkId)
            .pipe(
              map((resp) => {
                const groups = resp.body.data;
                console.log('Received network groups:', {
                  count: groups.length,
                  groups: groups.map((g: NetworkGroupModel) => ({
                    id: g.networkGroupId,
                    name: g.displayName,
                  })),
                });
                this.availableGroups.set(groups);
                return groups;
              })
            );
        })
      )
      .subscribe();

    this.networkGroupService.attributionEntityAndGroup$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([entity, attributionGroup]) => {
        this.isLocked = entity !== null;
        this.attributionGroup.set(attributionGroup);
        this.tooltipText = entity
          ? `Cannot change while viewing ${entity}`
          : '';
        this.cdr.markForCheck();
      });
  }

  /**
   * Select a network group
   */
  selectGroup(group: NetworkGroupModel): void {
    console.log('Setting network group:', group);
    this.networkGroupService.getNetworkGroup(
      group.networkGroupId,
      group.networkId,
      false
    );
  }

  /**
   * Select a network group and navigate to home
   */
  selectGroupAndNavigate(group: NetworkGroupModel): void {
    this.networkGroupHeaderService.networkGroupId.set(group.networkGroupId);
    this.router.navigate(['/']);
  }

  /**
   * Select the network (clear group selection)
   */
  selectNetwork(): void {
    console.log('Clearing network group');
    this.networkGroupService.setSelectedGroup(null);
  }
}
